import { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import api from "../../api";
import { toast } from "react-toastify";

export default function AdminJuryPage() {
  const [jury,       setJury]       = useState(null);
  const [events,     setEvents]     = useState([]);
  const [form,       setForm]       = useState({ name: "", email: "", password: "" });
  const [saving,     setSaving]     = useState(false);
  const [assignForm, setAssignForm] = useState({ juryId: null, eventId: "" });
  const [pwdModal,       setPwdModal]       = useState(null);
  const [newPwd,         setNewPwd]         = useState("");
  const [showPwd,        setShowPwd]        = useState(false);
  const [pwdSaving,      setPwdSaving]      = useState(false);
  const [showForm,       setShowForm]       = useState(false);
  const [deleteConfirm,  setDeleteConfirm]  = useState(null); // { id, name }
  const [search,         setSearch]         = useState("");

  const fetchJury   = () => api.get("/jury").then(r => setJury(r.data.data || [])).catch(() => setJury([]));
  const fetchEvents = () => api.get("/jury/events").then(r => setEvents(r.data.data || [])).catch(() => {});
  useEffect(() => { fetchJury(); fetchEvents(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/jury", form);
      toast.success("Jury member created");
      setForm({ name: "", email: "", password: "" });
      setShowForm(false);
      fetchJury();
    } catch (err) { toast.error(err.response?.data?.error || "Failed to create"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/jury/${id}`); toast.success("Deleted"); fetchJury(); }
    catch { toast.error("Failed"); }
    setDeleteConfirm(null);
  };

  const handleToggle = async (id, isActive) => {
    try {
      await api.patch(`/jury/${id}/toggle`, { isActive: !isActive });
      toast.success(`Jury member ${!isActive ? "activated" : "deactivated"}`);
      fetchJury();
    } catch { toast.error("Failed"); }
  };

  const handleAssign = async () => {
    if (!assignForm.eventId) return;
    try {
      await api.patch(`/jury/${assignForm.juryId}/assign-event`, { eventId: assignForm.eventId });
      toast.success("Event assigned"); setAssignForm({ juryId: null, eventId: "" }); fetchJury();
    } catch (err) { toast.error(err.response?.data?.error || "Failed"); }
  };

  const handleUnassign = async (juryId, eventId) => {
    try { await api.patch(`/jury/${juryId}/unassign-event`, { eventId }); toast.success("Event removed"); fetchJury(); }
    catch { toast.error("Failed"); }
  };

  const handleUpdatePassword = async () => {
    if (!newPwd.trim() || newPwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setPwdSaving(true);
    try {
      await api.patch(`/jury/${pwdModal.juryId}/password`, { password: newPwd });
      toast.success("Password updated"); setPwdModal(null); setNewPwd(""); setShowPwd(false);
    } catch (err) { toast.error(err.response?.data?.error || "Failed"); }
    finally { setPwdSaving(false); }
  };

  const activeCount   = (jury || []).filter(j => j.isActive).length;
  const inactiveCount = (jury || []).filter(j => !j.isActive).length;

  const filteredJury = (jury || []).filter(j => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return j.name.toLowerCase().includes(q) || j.email.toLowerCase().includes(q);
  });

  return (
    <Layout title="Jury Members" subtitle="Manage jury panel members and their event assignments">

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, maxWidth: 360 }}>
            <div style={{ fontSize: 36, marginBottom: 12, textAlign: "center" }}>🗑</div>
            <h3 style={{ ...modalTitle, textAlign: "center" }}>Delete Jury Member?</h3>
            <p style={{ ...modalSub, textAlign: "center", marginBottom: 20 }}>
              Permanently delete <strong>{deleteConfirm.name}</strong>? Their scores will also be removed and averages recomputed.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setDeleteConfirm(null)} style={btnOutline}>Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} style={btnDanger}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Password modal */}
      {pwdModal && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div style={{ marginBottom: 20 }}>
              <h3 style={modalTitle}>Reset Password</h3>
              <p style={modalSub}>for <strong>{pwdModal.name}</strong></p>
            </div>
            <label style={lbl}>New Password</label>
            <div style={{ position: "relative", marginBottom: 20 }}>
              <input
                type={showPwd ? "text" : "password"} autoFocus
                value={newPwd} onChange={e => setNewPwd(e.target.value)}
                placeholder="Minimum 6 characters"
                className="voh-input"
                style={{ paddingRight: 60 }}
              />
              <button type="button" onClick={() => setShowPwd(v => !v)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 12, fontWeight: 600 }}>
                {showPwd ? "Hide" : "Show"}
              </button>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => { setPwdModal(null); setNewPwd(""); setShowPwd(false); }} style={btnOutline}>Cancel</button>
              <button onClick={handleUpdatePassword} disabled={pwdSaving} style={btnPrimary(pwdSaving)}>
                {pwdSaving ? "Saving…" : "Update Password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats row */}
      {jury && (
        <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: "Total",    value: jury.length,   color: "#1D9AEC", bg: "#EFF8FF" },
            { label: "Active",   value: activeCount,   color: "#10B981", bg: "#ECFDF5" },
            { label: "Inactive", value: inactiveCount, color: "#94A3B8", bg: "#F8FAFC" },
          ].map(s => (
            <div key={s.label} style={{
              background: s.bg, border: `1px solid ${s.color}22`,
              borderRadius: 10, padding: "14px 20px",
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <span style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: s.color, opacity: 0.8 }}>{s.label}</span>
            </div>
          ))}

          {/* Search */}
          <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
            <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94A3B8" }}>🔍</span>
            <input
              type="text" placeholder="Search by name or email…" value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", padding: "9px 14px 9px 34px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, boxSizing: "border-box", outline: "none", background: "#fff", fontFamily: "inherit", color: "#0F172A" }}
              onFocus={e => { e.target.style.borderColor = "#1D9AEC"; }}
              onBlur={e =>  { e.target.style.borderColor = "#E2E8F0"; }}
            />
            {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 14 }}>✕</button>}
          </div>

          <button
            onClick={() => setShowForm(v => !v)}
            style={{
              marginLeft: "auto",
              background: showForm ? "#F1F5F9" : "linear-gradient(135deg, #1D9AEC, #1585D8)",
              color: showForm ? "#475569" : "#fff",
              border: showForm ? "1px solid #E2E8F0" : "none",
              padding: "10px 20px", borderRadius: 9,
              fontWeight: 700, fontSize: 13.5, cursor: "pointer",
              boxShadow: showForm ? "none" : "0 4px 12px rgba(29,154,236,0.3)",
              transition: "all 0.2s",
              alignSelf: "center",
            }}
          >
            {showForm ? "✕ Cancel" : "+ Add Jury Member"}
          </button>
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div style={{ ...card, marginBottom: 20, borderTop: "3px solid #1D9AEC", animation: "fadeIn 0.2s ease" }}>
          <h3 style={{ margin: "0 0 18px", fontSize: 15, fontWeight: 700, color: "#0F172A" }}>New Jury Member</h3>
          <form onSubmit={handleCreate}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 12, alignItems: "end" }}>
              {[["Name", "name", "text", "Full name"],
                ["Email", "email", "email", "email@example.com"],
                ["Password", "password", "password", "Min. 6 characters"]].map(([label, key, type, ph]) => (
                <div key={key}>
                  <label style={lbl}>{label}</label>
                  <input
                    type={type} required placeholder={ph}
                    value={form[key]}
                    onChange={e => setForm({ ...form, [key]: e.target.value })}
                    className="voh-input"
                  />
                </div>
              ))}
              <button type="submit" disabled={saving} style={{ ...btnPrimary(saving), height: 42, paddingLeft: 22, paddingRight: 22 }}>
                {saving ? "…" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Loading */}
      {!jury && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ ...card, height: 100 }}>
              <div className="skeleton" style={{ height: 14, width: "30%", marginBottom: 8 }} />
              <div className="skeleton" style={{ height: 10, width: "20%" }} />
            </div>
          ))}
        </div>
      )}

      {/* Jury list */}
      {jury && jury.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px", background: "#fff", borderRadius: 10, border: "1px solid #E2E8F0" }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>👥</div>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#374151", margin: "0 0 6px" }}>No jury members yet</h3>
          <p style={{ fontSize: 13, color: "#94A3B8" }}>Click "+ Add Jury Member" to get started.</p>
        </div>
      )}

      {search && jury && (
        <p style={{ fontSize: 12, color: "#94A3B8", marginBottom: 10 }}>
          Showing <strong style={{ color: "#374151" }}>{filteredJury.length}</strong> of <strong style={{ color: "#374151" }}>{jury.length}</strong> jury members
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {filteredJury.length === 0 && search ? (
          <div style={{ textAlign: "center", padding: "40px 20px", background: "#fff", borderRadius: 10, border: "1px solid #E2E8F0" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
            <p style={{ fontWeight: 600, color: "#374151", margin: "0 0 4px" }}>No results for "{search}"</p>
          </div>
        ) : null}
        {filteredJury.map(j => (
          <div key={j._id} style={{
            ...card,
            borderLeft: `3px solid ${j.isActive ? "#10B981" : "#E2E8F0"}`,
            opacity: j.isActive ? 1 : 0.75,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>

              {/* Info */}
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
                  background: j.isActive
                    ? "linear-gradient(135deg, #1D9AEC, #6366F1)"
                    : "linear-gradient(135deg, #CBD5E1, #94A3B8)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: 17, color: "#fff",
                }}>
                  {j.name[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: "#0F172A" }}>{j.name}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                      background: j.isActive ? "#ECFDF5" : "#F1F5F9",
                      color: j.isActive ? "#059669" : "#94A3B8",
                      border: `1px solid ${j.isActive ? "#A7F3D0" : "#E2E8F0"}`,
                      textTransform: "uppercase", letterSpacing: "0.05em",
                    }}>
                      {j.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <span style={{ fontSize: 12.5, color: "#64748B" }}>{j.email}</span>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                <button onClick={() => handleToggle(j._id, j.isActive)}
                  style={j.isActive ? btnWarning : btnSuccess}>
                  {j.isActive ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => setAssignForm(assignForm.juryId === j._id ? { juryId: null, eventId: "" } : { juryId: j._id, eventId: "" })}
                  style={btnPurple}>
                  + Event
                </button>
                <button onClick={() => { setPwdModal({ juryId: j._id, name: j.name }); setNewPwd(""); }}
                  style={btnCyan}>
                  Reset Pwd
                </button>
                <button onClick={() => setDeleteConfirm({ id: j._id, name: j.name })} style={btnDanger}>Delete</button>
              </div>
            </div>

            {/* Assigned events */}
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #F1F5F9" }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 10 }}>
                Events
              </span>
              <div style={{ display: "inline-flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {(j.assignedEvents || []).length === 0 ? (
                  <span style={{ fontSize: 12.5, color: "#CBD5E1", fontStyle: "italic" }}>No events assigned</span>
                ) : j.assignedEvents.map(ev => (
                  <span key={ev._id} style={{
                    background: "#EFF8FF", color: "#0369A1",
                    border: "1px solid #BFDBFE",
                    padding: "4px 10px", borderRadius: 20,
                    fontSize: 12, fontWeight: 600,
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}>
                    {ev.eventName}
                    <button onClick={() => handleUnassign(j._id, ev._id)} style={{
                      background: "none", border: "none", color: "#EF4444",
                      cursor: "pointer", fontWeight: 700, fontSize: 13,
                      padding: 0, lineHeight: 1, display: "flex",
                    }}>×</button>
                  </span>
                ))}
              </div>
            </div>

            {/* Assign event dropdown */}
            {assignForm.juryId === j._id && (
              <div style={{ marginTop: 12, display: "flex", gap: 8, animation: "fadeIn 0.2s ease" }}>
                <select
                  value={assignForm.eventId}
                  onChange={e => setAssignForm({ ...assignForm, eventId: e.target.value })}
                  className="voh-input"
                  style={{ flex: 1 }}
                >
                  <option value="">— Select event to assign —</option>
                  {events
                    .filter(ev => !(j.assignedEvents || []).some(a => a._id === ev._id))
                    .map(ev => <option key={ev._id} value={ev._id}>{ev.eventName}</option>)
                  }
                </select>
                <button onClick={handleAssign} style={btnPrimary(false)}>Assign</button>
                <button onClick={() => setAssignForm({ juryId: null, eventId: "" })} style={btnOutline}>Cancel</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}

/* ── Shared styles ── */
const card        = { background: "#fff", borderRadius: 10, padding: "18px 20px", boxShadow: "var(--shadow-sm)", border: "1px solid #E2E8F0" };
const lbl         = { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6, letterSpacing: "-0.01em" };
const modalOverlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" };
const modalBox     = { background: "#fff", borderRadius: 14, padding: 28, width: 380, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" };
const modalTitle   = { margin: 0, fontSize: 17, fontWeight: 700, color: "#0F172A" };
const modalSub     = { margin: "4px 0 0", fontSize: 13, color: "#64748B" };

const btnBase = (bg, color = "#fff") => ({
  background: bg, color, border: "none",
  padding: "6px 13px", borderRadius: 7,
  cursor: "pointer", fontSize: 12.5, fontWeight: 600,
  whiteSpace: "nowrap", transition: "opacity 0.15s",
});
const btnPrimary = (disabled) => ({
  ...btnBase(disabled ? "#94A3B8" : "linear-gradient(135deg, #1D9AEC, #1585D8)"),
  cursor: disabled ? "not-allowed" : "pointer",
  boxShadow: disabled ? "none" : "0 2px 8px rgba(29,154,236,0.3)",
  padding: "9px 18px", fontSize: 13,
});
const btnOutline = { ...btnBase("transparent", "#475569"), border: "1px solid #E2E8F0" };
const btnWarning = btnBase("#F59E0B");
const btnSuccess = btnBase("#10B981");
const btnPurple  = btnBase("#6366F1");
const btnCyan    = btnBase("#0891B2");
const btnDanger  = btnBase("#EF4444");
